import { onMounted, onUnmounted, readonly, ref } from 'vue';

/**
 * Viewport helpers.
 *
 * `client/js/helpers.js` exported a one-shot `isMobile()` that read
 * `window.innerWidth` once and published the result as `window.isMobile`. In a
 * single-page app the value has to stay live across resizes and orientation
 * changes, so it is reactive here.
 */

/** Matches `$breakpoint-md` in the stylesheets. */
export const BREAKPOINT_MD = 1023;
/** Matches `$breakpoint-sm`. */
export const BREAKPOINT_SM = 767;

export function useIsMobile(breakpoint: number = BREAKPOINT_MD) {
  const isMobile = ref(false);
  let query: MediaQueryList | undefined;

  function update(event: MediaQueryList | MediaQueryListEvent) {
    isMobile.value = event.matches;
  }

  onMounted(() => {
    query = window.matchMedia(`(max-width: ${breakpoint}px)`);
    update(query);
    query.addEventListener('change', update);
  });

  onUnmounted(() => {
    query?.removeEventListener('change', update);
  });

  return readonly(isMobile);
}

let scrollLocks = 0;

/**
 * Body scroll locking for modals and the mobile menu.
 *
 * Replaces `disableBodyScroll` / `enableBodyScroll` from helpers.js. The counter
 * matters because the old version was a plain class toggle, so two overlapping
 * consumers (menu open behind a modal) would unlock the page too early.
 */
export function useBodyScrollLock() {
  let held = false;

  function lock() {
    if (held) {
      return;
    }
    held = true;
    scrollLocks += 1;
    document.body.classList.add('disable-scroll');
  }

  function unlock() {
    if (!held) {
      return;
    }
    held = false;
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
      document.body.classList.remove('disable-scroll');
    }
  }

  onUnmounted(unlock);

  return { lock, unlock };
}
